import { store, store$ } from '@redux/store';
import { selectIsLoggedIn } from 'features/userProfile';
import messageService from 'layout/services/messageService';
import modalService from 'layout/services/modalService';
import { distinctUntilChanged, distinctUntilKeyChanged, filter, map } from 'rxjs';
import circularProgress from '@icons/animations/circular-progress.json';
import { selectModalListingInformation } from 'layout/redux/selectors';
import toBoolean from 'utils/toBoolean';
import { decode } from 'utils/phoneCipher';
import ThankScreenVariation from '../../searchResultsFacets/enums/thankScreenVariation';
import { getContactBuilderAnchor, getInternalBrowsingAnchor } from '../services/anchorConstructor';
import createLeadGenerator from '../services/leadGeneratorConstructor';
import { initialState, updateModalProgress } from '../services/taskFlow';
import RecommendedScreen from './recommendedScreen';
import RecommendedScreenBasic from './recommendedScreenBasic';
import ThankScreenAskQuestion from './thankScreenAskQuestion';
import ThankScreenBasic from './thankScreenBasic';
import ThankScreenBrochure from './thankScreenBrochure';
import ThankScreenComingSoon from './thankScreenComingSoon';
import ThankScreenInformation from './thankScreenInformation';
import ThankScreenTour from './thankScreenTour';

const message$ = messageService.getMessage();
const isThankYouScreen = (state) => state?.uiState?.modalInformation?.name === 'thankYouScreen';
const isComingSoon = (state) => selectModalListingInformation(state)?.isComingSoon ?? false;
const isRecommendationsScreen = (state) => state?.uiState?.modalInformation?.name === 'recommendedScreen';
const isRecommendationsScreenBasic = (state) => state?.uiState?.modalInformation?.name === 'recommendedScreenBasic';
const hasFinishedLoading = (state) => !state?.uiState?.isFetchingModal;

const leadGeneratorInstance = {
  generator: null,
  state: null,
};
const recommendationsInstance = {
  generator: null,
};

let lottieInstance = null;

/**
 * Loads and plays a Lottie animation inside the specified container element.
 *
 * Dynamically imports the 'lottie-web' library if not already loaded,
 * then initializes and plays a looping, autoplaying SVG animation using the provided animation data.
 *
 * @async
 * @param {HTMLElement} container - The DOM element in which to render the Lottie animation.
 * @returns {Promise<void>} Resolves when the animation is loaded and started.
 */
async function loadLottieAnimation(container) {
  if (!lottieInstance) {
    const lottie = await import('lottie-web');
    lottieInstance = lottie;
  }

  lottieInstance.loadAnimation({
    container,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    animationData: circularProgress,
  });
}

/**
 * Returns a Free Brochure Thank You screen.
 *
 * @param {Object} modalInformation - Information about the modal.
 * @returns {ThankScreenBrochure} - The Thank You screen for a free brochure request.
 */
function getFreeBrochureThankYouScreen(modalInformation) {
  const brochureThankYouScreen = new ThankScreenBrochure(modalInformation);

  return brochureThankYouScreen;
}

/**
 * Returns a Coming Soon Thank You screen.
 *
 * @param {Object} modalInformation - Information about the modal.
 * @returns {ThankScreenBrochure} - The Thank You screen for a coming soon brochure request.
 */
function getComingSoonThankYouScreen(modalInformation, isLoggedIn) {
  const comingSoonThankYouScreen = new ThankScreenComingSoon(modalInformation);
  const contactBuilderAnchor = getContactBuilderAnchor(modalInformation);
  const internalBrowsingAnchor = getInternalBrowsingAnchor(isLoggedIn);
  comingSoonThankYouScreen.setContactBuilderAnchor(contactBuilderAnchor);
  comingSoonThankYouScreen.setInternalBrowsingAnchor(internalBrowsingAnchor);

  return comingSoonThankYouScreen;
}

/**
 * Returns a Thank You Basic screen.
 *
 * @param {Object} modalInformation - Information about the modal.
 * @returns {ThankScreenBasic} - The Thank You Basic screen.
 */
function getThankScreenBasicScreen(modalInformation, isLoggedIn) {
  const thankYouScreenBasic = new ThankScreenBasic(modalInformation);
  const internalBrowsingAnchor = getInternalBrowsingAnchor(isLoggedIn);
  thankYouScreenBasic.setInternalBrowsingAnchor(internalBrowsingAnchor);

  return thankYouScreenBasic;
}

/**
 * Decides which variation of the thank you screen to show and returns an instance of it.
 */
const getThankYouScreen = () => {
  const state = store.getState();
  const comingSoon = toBoolean(isComingSoon(state));
  const screenVariation = state?.uiState?.modalInformation?.variation;
  const effectiveScreenVariation = comingSoon ? ThankScreenVariation.ComingSoon.toString() : screenVariation;
  const modalInformation = state?.uiState?.modalInformation;
  const isLoggedIn = selectIsLoggedIn(state);

  switch (effectiveScreenVariation) {
    case ThankScreenVariation.ComingSoon.toString():
      return getComingSoonThankYouScreen(modalInformation, isLoggedIn);
    case ThankScreenVariation.AskQuestion.toString():
      return new ThankScreenAskQuestion();
    case ThankScreenVariation.Brochure.toString():
      return getFreeBrochureThankYouScreen(modalInformation);
    case ThankScreenVariation.Tour.toString():
      return new ThankScreenTour(modalInformation);
    case ThankScreenVariation.Basic.toString():
      return getThankScreenBasicScreen(modalInformation, isLoggedIn);
    case ThankScreenVariation.Information.toString():
      return new ThankScreenInformation(modalInformation);
    default:
      // if no version is provided, we default to the free brochure one.
      return getFreeBrochureThankYouScreen(modalInformation);
  }
};

/**
 * This function takes the user to a "thank you screen". This screen is different
 * depending on which flow the user is following, so we receive an argument indicating
 * the name of the thank you screen to show.
 * @param {string} thankYouScreen is the name of the thank you screen to show.
 */
const showThankYouScreen = () => {
  const screen = getThankYouScreen();

  modalService.onHtmlRemoved({
    name: 'thankYouScreen',
    fn: screen.unbindEvents?.bind(screen),
  });

  modalService.onHtmlReady({
    name: 'thankYouScreen',
    fn: screen.bindEvents.bind(screen),
  });

  modalService.setHtml({ payload: screen.getHtml() });
};

const showRecommendationsScreen = (data) => {
  recommendationsInstance.generator = new RecommendedScreen(data?.uiState?.modalInformation?.html);

  modalService.onHtmlReady({
    name: 'recommendedScreen',
    fn: recommendationsInstance.generator.bindEvents.bind(recommendationsInstance.generator),
  });

  modalService.onHtmlRemoved({
    name: 'recommendedScreen',
    fn: recommendationsInstance.generator.unbindEvents.bind(recommendationsInstance.generator),
  });

  modalService.setHtml({ payload: recommendationsInstance.generator.getHtml() });
};

const showRecommendationsScreenBasic = (data) => {
  recommendationsInstance.generator = new RecommendedScreenBasic(data?.uiState?.modalInformation?.html);

  modalService.onHtmlReady({
    name: 'recommendedScreenBasic',
    fn: recommendationsInstance.generator.bindEvents.bind(recommendationsInstance.generator),
  });

  modalService.onHtmlRemoved({
    name: 'recommendedScreenBasic',
    fn: recommendationsInstance.generator.unbindEvents.bind(recommendationsInstance.generator),
  });

  modalService.setHtml({ payload: recommendationsInstance.generator.getHtml() });
};

const parserCardAttributes = (attributes) => {
  const properties = { ...attributes };
  const phoneNumber = decode(properties.phoneNumber, properties.cipherShift);
  properties.phoneNumber = phoneNumber;
  delete properties.cipherShift;
  return properties;
};

document.body.addEventListener('click', (event) => {
  const leadGeneratorOpener = event.target.closest('[data-lead-generator-opener]');
  const properties = { ...leadGeneratorOpener?.dataset };
  const openerKey = properties?.leadGeneratorOpener;
  const leadGenerator = createLeadGenerator(openerKey, properties);

  if (!leadGenerator) {
    return false;
  }

  const cardContainer = event.target.closest('[data-map-card]') || event.target.closest('[data-card]');
  const listingInformation = parserCardAttributes(cardContainer?.dataset);
  // Here we are checking all the values from the data set, and we are going to use the ones that are not empty.
  const validListingInformation = Object.fromEntries(
    Object.entries(listingInformation).filter(([, value]) => Boolean(value)),
  );
  properties.listingInformation = validListingInformation;
  const getGoogleAnalyticsEventCallback = leadGenerator.constructor.getFormOpenedAnalyticsData;
  leadGenerator.excludeRecommendations = properties?.excludeRecommendations === 'true';
  const contactVendorName = properties?.listingInformation?.contactVendorName ?? '';
  leadGenerator.setPrivacyPolicyText?.(properties?.phoneNumber, contactVendorName);
  leadGenerator.setHousingData?.(properties);

  leadGeneratorInstance.state = initialState(properties, getGoogleAnalyticsEventCallback);
  leadGeneratorInstance.generator = leadGenerator;

  modalService.onHtmlReady({
    name: 'leadGenerator',
    fn: leadGeneratorInstance.generator.bindEvents.bind(leadGeneratorInstance.generator),
  });

  modalService.onHtmlRemoved({
    name: 'leadGenerator',
    fn: leadGeneratorInstance.generator.unbindEvents.bind(leadGeneratorInstance.generator),
  });

  modalService.setHtml({ payload: leadGeneratorInstance.generator.getHtml() });
  return true;
});

document.body.addEventListener('click', (event) => {
  const backButton = event.target.closest('.modal__back');
  const closeButton = event.target.closest('#nhs_modalClose');
  const overlayElement = event.target.closest('#overlayElement');

  if (backButton && leadGeneratorInstance.state) {
    updateModalProgress(leadGeneratorInstance.state);
    modalService.setPreviousHtml();
    return;
  }

  // Only proceed with clearing if user did not click overlay for Thank You screen
  if (leadGeneratorInstance.state) {
    const isThankYou = toBoolean(isThankYouScreen(store.getState()));
    const isRecommendation = toBoolean(isRecommendationsScreen(store.getState()));
    const isComingSoonStatus = toBoolean(isComingSoon(store.getState()));

    const isCloseClick = !!closeButton;
    const isOverlayClick = !!overlayElement;

    // Overlay click is allowed if:
    // 1. Thank You screen is shown AND it's a Coming Soon listing
    const isComingSoonThankYou = isOverlayClick && isComingSoonStatus && isThankYou;

    // 2. Recommendation screen is shown AND it's a Coming Soon listing
    const isComingSoonRecommendation = isOverlayClick && isComingSoonStatus && isRecommendation;

    // 3. Any other modal (not Thank You or Recommendation)
    const isOverlayOutsideModal = isOverlayClick && !isThankYou && !isRecommendation;

    // Final decision to clear modal HTML
    if (isCloseClick || isComingSoonThankYou || isComingSoonRecommendation || isOverlayOutsideModal) {
      // Clear the modal HTML only if the click is allowed based on the above conditions
      leadGeneratorInstance.generator = null;
      leadGeneratorInstance.state = null;
      modalService.clearHtml();
    }

    // Do nothing on overlay click if we're on the Thank You screen
    if (overlayElement && isThankYou) {
      return;
    }

    // Show Thank You screen if on recommendation screen and overlay is clicked
    if (overlayElement && isRecommendation) {
      if (!isComingSoonStatus) {
        showThankYouScreen();

        const animationContainer = document.querySelector('#download-animation-container');
        if (animationContainer) {
          loadLottieAnimation(animationContainer);
        }
      } else {
        const skipButton = document.querySelector('[data-skip-recommendations]');
        skipButton?.click();
      }
    }
  }
});

store$.pipe(map(isThankYouScreen), distinctUntilChanged(), filter(Boolean)).subscribe(showThankYouScreen);

store$
  .pipe(filter(isRecommendationsScreen), filter(hasFinishedLoading), distinctUntilKeyChanged('uiState'))
  .subscribe(showRecommendationsScreen);

store$
  .pipe(filter(isRecommendationsScreenBasic), filter(hasFinishedLoading), distinctUntilKeyChanged('uiState'))
  .subscribe(showRecommendationsScreenBasic);

message$
  .pipe(
    filter((msg) => msg.messageType === 'leadPosted'),
    filter((msg) => msg.payload.html !== ''),
    filter((message) => !message.isBasic),
    map((data) => data.payload),
  )
  .subscribe((data) => {
    const modalInformation = {
      name: 'recommendedScreen',
      html: data.html,
    };

    updateModalProgress(modalInformation);
  });

message$
  .pipe(
    filter((msg) => msg.messageType === 'leadPosted'),
    filter((msg) => msg.payload.html !== ''),
    filter((message) => message.isBasic),
    map((data) => data.payload),
  )
  .subscribe((data) => {
    const modalInformation = {
      name: 'recommendedScreenBasic',
      html: data.html,
    };

    updateModalProgress(modalInformation);
  });

message$
  .pipe(
    filter((msg) => msg.messageType === 'leadPosted'),
    filter((msg) => msg.payload.html === ''),
  )
  .subscribe(() => {
    const modalInformation = {
      name: 'thankYouScreen',
    };

    updateModalProgress(modalInformation);
  });
